import {
  DXF_ENTITY_TYPE,
  DXF_PMI_ENTITY_TYPES,
  type DxfEntity,
  type DxfScene,
  type DxfText,
} from '@frontend/api';
import {
  type Aabb2,
  type Circle2,
  type Loop,
  type Point2,
  type PolylineInput,
  buildLoopIndex,
  buildSnapIndex,
  flattenedCurveCenter,
  flattenedCurveCircle,
  flattenedRunCircle,
  flattenedShapeCenter,
  polylineBounds,
  stitchLoops,
  unionAabb,
} from '@frontend/viewer-core';

import type {
  ChunkAddress,
  DrawingModel,
  MeasureEdge,
  MeasureSnapCandidate,
  ModelText,
} from './types';

/**
 * The stable address of the *source entity* a primitive came from.
 *
 * `h` is the top-level handle, shared by every primitive one INSERT or DIMENSION
 * exploded into; `src` is the block-definition chain that disambiguates within it.
 * Together they are what the vocabulary calls an Entity — the unit roles are keyed
 * by, and the unit a layer's count should report.
 */
export const entityKey = (entity: Pick<DxfEntity, 'h' | 'src'>): string =>
  entity.src?.length ? `${entity.h}/${entity.src.join('/')}` : entity.h;

/**
 * Content fingerprint for a scene text row — source entity, pose, and string.
 *
 * Not unique on its own: identical labels stacked on the same pose share a base
 * key. `deriveModel` disambiguates collisions with an occurrence suffix.
 */
export const textKey = (text: DxfText): string => {
  const [x, y] = text.pos;
  return `${entityKey(text)}|${x},${y}|${text.width}x${text.height}|${
    text.rotation
  }|${text.s}`;
};

/**
 * Assigns ids unique within one derivation, appending `#n` when `textKey` collides.
 *
 * The first row keeps the bare fingerprint; later collisions become `#1`, `#2`, …
 * so React keys and future addressing never invent an index at render time.
 */
const assignTextIds = (sceneTexts: readonly DxfText[]): ModelText[] => {
  const seen = new Map<string, number>();
  return sceneTexts.map((text) => {
    const base = textKey(text);
    const occurrence = seen.get(base) ?? 0;
    seen.set(base, occurrence + 1);
    return {
      id: occurrence === 0 ? base : `${base}#${occurrence}`,
      text,
    };
  });
};

/**
 * Fraction of the drawing's diagonal within which two endpoints are the same point.
 *
 * Relative rather than absolute because a drawing may be in millimetres or in
 * inches, and a fixed epsilon would either weld nothing in one or weld distinct
 * features together in the other. A millionth of the diagonal is far below any
 * feature a person drew and far above the rounding a CAD exporter leaves behind.
 */
const STITCH_TOLERANCE_FRACTION = 1e-6;

/** Circular geometry: three points on it determine its centre exactly. */
const CIRCULAR_ENTITY_TYPES: ReadonlySet<string> = new Set([
  DXF_ENTITY_TYPE.CIRCLE,
  DXF_ENTITY_TYPE.ARC,
]);

/**
 * Non-circular curves whose centre is the middle of their extent.
 *
 * An ellipse has to be handled this way: three points on one do not determine
 * its centre, and running a circumcenter over them lands somewhere arbitrary.
 */
const EXTENT_CENTER_ENTITY_TYPES: ReadonlySet<string> = new Set([
  DXF_ENTITY_TYPE.ELLIPSE,
]);

/**
 * Row types that are a measure line when they have exactly two points.
 *
 * A polyline with more vertices, or with a bulge the backend flattened into
 * more points, is a curve: telling its straight runs from its arcs would need
 * the backend to say which was which.
 */
const STRAIGHT_ENTITY_TYPES: ReadonlySet<string> = new Set([
  DXF_ENTITY_TYPE.LINE,
  DXF_ENTITY_TYPE.LWPOLYLINE,
  DXF_ENTITY_TYPE.POLYLINE,
]);

const CURVE: MeasureEdge = { kind: 'curve' };

type CircleEdge = Extract<MeasureEdge, { kind: 'circle' }>;

/**
 * How far apart arcs' centres and radii may be, as a fraction of the radius,
 * and still close one circle. Scene coordinates are rounded to four decimal
 * places, which on a small hole moves a centre found from them by about 1e-4.
 */
const ARC_CIRCLE_FRACTION = 0.01;

/** Row types whose flattened points may all be one arc: a bulge or a spline. */
const SINGLE_ARC_ENTITY_TYPES: ReadonlySet<string> = new Set([
  DXF_ENTITY_TYPE.LWPOLYLINE,
  DXF_ENTITY_TYPE.POLYLINE,
  DXF_ENTITY_TYPE.SPLINE,
]);

/**
 * Fewest points a row may have and still be a single arc.
 *
 * A scene that predates `lines` leaves it off straight-sided polygons too, and
 * a regular polygon's vertices all lie on one circle. The backend flattens each
 * bulge or spline piece as Béziers of at most a quarter turn, each into at
 * least four segments, so a full circle arrives with at least seventeen points,
 * where a hexagon or an octagon has seven or nine. The minimum sits just below
 * a full circle's.
 */
const MIN_SINGLE_ARC_POINTS = 16;

/**
 * The circle an ARC row, or a row whose points are all one arc, lies on.
 *
 * A row that names any straight segment is never one arc, and neither is a row
 * with any point off the fitted circle.
 */
const arcCircleOf = (row: DxfEntity): Circle2 | null => {
  const points = row.pts as Point2[];
  if (row.t === DXF_ENTITY_TYPE.ARC) {
    return flattenedCurveCircle(points);
  }
  return SINGLE_ARC_ENTITY_TYPES.has(row.t) &&
    !row.lines &&
    points.length >= MIN_SINGLE_ARC_POINTS
    ? flattenedRunCircle(points, ARC_CIRCLE_FRACTION)
    : null;
};

/**
 * What a scene row is to the Measure tool on its own. Only CIRCLE rows are
 * circles here: an arc, or a polyline or spline that is all one arc, has a
 * centre to snap to, but no caliper rule unless it closes a circle, alone or
 * with others (see `circleClosedByArcs`).
 */
const measureEdgeOf = (entity: DxfEntity): MeasureEdge => {
  const points = entity.pts as Point2[];
  if (STRAIGHT_ENTITY_TYPES.has(entity.t) && points.length === 2) {
    return { kind: 'line', segment: [points[0], points[1]] };
  }
  const circle =
    entity.t === DXF_ENTITY_TYPE.CIRCLE ? flattenedCurveCircle(points) : null;
  return circle ? { kind: 'circle', ...circle } : CURVE;
};

/**
 * The circle that the arcs of one closed loop draw between them, or `null`,
 * given each row's `arcCircleOf`.
 *
 * A hole is often drawn as two half arcs, or as a closed polyline of bulges,
 * and measures like a CIRCLE. Stitching has already joined the arcs end to
 * end, so arcs sharing a centre and radius that close a loop go all the way
 * round. A slot or a fillet joins lines, so its loop is not all arcs and its
 * arcs stay curves.
 */
const circleClosedByArcs = (
  arcCircles: readonly (Circle2 | null)[],
): CircleEdge | null => {
  const circles = arcCircles.filter(
    (circle): circle is Circle2 => circle !== null,
  );
  const [first] = circles;
  if (!first || circles.length !== arcCircles.length) {
    return null;
  }
  const tolerance = first.radius * ARC_CIRCLE_FRACTION;
  const isSameCircle = circles.every(
    ({ center, radius }) =>
      Math.hypot(center[0] - first.center[0], center[1] - first.center[1]) <=
        tolerance && Math.abs(radius - first.radius) <= tolerance,
  );
  if (!isSameCircle) {
    return null;
  }

  const mean = (pick: (circle: (typeof circles)[number]) => number) =>
    circles.reduce((total, circle) => total + pick(circle), 0) / circles.length;
  return {
    kind: 'circle',
    center: [mean(({ center }) => center[0]), mean(({ center }) => center[1])],
    radius: mean(({ radius }) => radius),
  };
};

/** One edge for a whole row, or one per segment in the row's own order. */
type RowMeasureEdges = MeasureEdge | MeasureEdge[];

/**
 * A row that names its straight segments is decided segment by segment, so a
 * part outline drawn as one polyline still offers each of its sides as a line.
 */
const measureEdgesOf = (entity: DxfEntity): RowMeasureEdges => {
  if (!entity.lines) {
    return measureEdgeOf(entity);
  }
  const straight = new Set(entity.lines);
  const points = entity.pts as Point2[];
  return points
    .slice(1)
    .map((end, index) =>
      straight.has(index)
        ? { kind: 'line', segment: [points[index], end] }
        : CURVE,
    );
};

/**
 * Snap-grid cell size, as a fraction of the drawing's diagonal.
 *
 * Set from the drawing rather than from the pick radius: the radius changes with
 * zoom on every query, and rebuilding the grid per pointer move would cost far
 * more than scanning a few extra cells.
 */
const SNAP_CELL_FRACTION = 1 / 128;

const stitchToleranceFor = (bounds: Aabb2 | null): number => {
  if (!bounds) {
    return STITCH_TOLERANCE_FRACTION;
  }
  const diagonal = Math.hypot(bounds[2] - bounds[0], bounds[3] - bounds[1]);
  return diagonal > 0
    ? diagonal * STITCH_TOLERANCE_FRACTION
    : STITCH_TOLERANCE_FRACTION;
};

/**
 * Endpoints, midpoints and — for a flattened curve — the centre.
 *
 * Midpoints are per *entity* rather than per tessellated segment: a flattened arc
 * has dozens of segments and offering a snap at each of their midpoints would bury
 * the targets a person actually wants under noise.
 *
 * A line's midpoint and a CIRCLE's centre carry their measure edge, so a click
 * on one measures as the line or circle. Arcs that close a circle get theirs
 * once stitching has shown that they do. A row that is all one arc snaps like
 * an ARC, so none of its flattened segments offers a midpoint.
 */
const collectSnapCandidates = (
  entity: DxfEntity,
  arcCircle: Circle2 | null,
  into: MeasureSnapCandidate[],
): void => {
  const points = entity.pts;
  if (points.length === 0 || entity.fill) {
    return;
  }

  const { layer } = entity;
  const entityId = entityKey(entity);
  const first = points[0] as Point2;
  const last = points[points.length - 1] as Point2;
  into.push({ point: first, kind: 'endpoint', layer, entityId });
  if (points.length > 1) {
    into.push({ point: last, kind: 'endpoint', layer, entityId });
  }

  if (
    arcCircle ||
    CIRCULAR_ENTITY_TYPES.has(entity.t) ||
    EXTENT_CENTER_ENTITY_TYPES.has(entity.t)
  ) {
    const center =
      arcCircle?.center ??
      (CIRCULAR_ENTITY_TYPES.has(entity.t)
        ? flattenedCurveCenter(points as Point2[])
        : flattenedShapeCenter(points as Point2[]));
    if (center) {
      const edge = measureEdgeOf(entity);
      into.push({
        point: center,
        kind: 'center',
        layer,
        entityId,
        ...(edge.kind === 'circle' && { measureEdge: edge }),
      });
    }
    return;
  }

  if (points.length === 2) {
    const edge = measureEdgeOf(entity);
    into.push({
      point: [(first[0] + last[0]) / 2, (first[1] + last[1]) / 2],
      kind: 'midpoint',
      layer,
      entityId,
      ...(edge.kind === 'line' && { measureEdge: edge }),
    });
    return;
  }

  // A polyline's vertices are its corners; each straight run between them gets a
  // midpoint, which is what a user reaches for on a rectangle or a slot. A row
  // that names its straight segments offers none along its arcs.
  const straight = entity.lines && new Set(entity.lines);
  for (let index = 1; index < points.length; index += 1) {
    if (straight && !straight.has(index - 1)) {
      continue;
    }
    const a = points[index - 1] as Point2;
    const b = points[index] as Point2;
    into.push({
      point: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2],
      kind: 'midpoint',
      layer,
      entityId,
      ...(straight && { measureEdge: { kind: 'line', segment: [a, b] } }),
    });
  }
};

/**
 * Reduces a scene to the model the viewer works against.
 *
 * One pass over the entities produces every aggregate, because the drawings that
 * matter here run to tens of thousands of primitives and this runs synchronously
 * when a scene lands.
 *
 * `scene.bbox` is preferred for the overall bounds when the backend supplies it,
 * but per-layer bounds have to be measured either way — visibility-aware fitting
 * needs them, and the wire format carries no per-layer extents.
 */
export const deriveModel = (scene: DxfScene): DrawingModel => {
  const boundsByLayer = new Map<number, Aabb2>();
  const entityKeysByLayer = new Map<number, Set<string>>();
  const layersWithPmi = new Set<number>();
  const stitchable: PolylineInput[] = [];
  /** The scene row behind each `stitchable` input, by the same position. */
  const stitchableRows: DxfEntity[] = [];
  /** What each `stitchable` row is to the Measure tool, by the same position. */
  const stitchableEdges: RowMeasureEdges[] = [];
  /** The circle each `stitchable` row's arc lies on, by the same position. */
  const stitchableArcCircles: (Circle2 | null)[] = [];
  const snapCandidates: MeasureSnapCandidate[] = [];
  /** Where each arc's centre snap sits in `snapCandidates`, by `stitchable` position. */
  const arcCenterCandidates = new Map<number, number>();
  /** Source-entity keys whose geometry is dimension annotation. */
  const pmiEntityKeys = new Set<string>();
  /**
   * Distinct entity keys in scene order. A `Set` preserves insertion order, so
   * this is both the dedupe and the ordering — no second pass.
   */
  const entityKeys = new Set<string>();
  const entityTypesByKey = new Map<string, Set<string>>();
  const textEntityKeys = new Set<string>();

  const recordEntityType = (key: string, entityType: string) => {
    const types = entityTypesByKey.get(key);
    if (types) {
      types.add(entityType);
      return;
    }
    entityTypesByKey.set(key, new Set([entityType]));
  };

  scene.entities.forEach((entity) => {
    if (entity.pts.length === 0) {
      return;
    }

    entityKeys.add(entityKey(entity));
    recordEntityType(entityKey(entity), entity.t);

    const arcCircle = entity.fill ? null : arcCircleOf(entity);

    // Fills are regions, not paths: a hatch has no meaningful chain to stitch into
    // and would drag a loop's outline through its interior. Its bounds and its
    // entity count still count, so it is excluded here rather than skipped above.
    if (!entity.fill && entity.pts.length >= 2) {
      stitchable.push({
        entityId: entityKey(entity),
        layer: entity.layer,
        points: entity.pts,
      });
      stitchableRows.push(entity);
      stitchableEdges.push(measureEdgesOf(entity));
      stitchableArcCircles.push(arcCircle);
    }

    // Holes sit inside their ring by construction, so they cannot widen bounds.
    const measured = polylineBounds(entity.pts);
    if (measured) {
      const merged = unionAabb(boundsByLayer.get(entity.layer), measured);
      if (merged) {
        boundsByLayer.set(entity.layer, merged);
      }
    }

    let keys = entityKeysByLayer.get(entity.layer);
    if (!keys) {
      keys = new Set();
      entityKeysByLayer.set(entity.layer, keys);
    }
    keys.add(entityKey(entity));

    if (DXF_PMI_ENTITY_TYPES.has(entity.t)) {
      layersWithPmi.add(entity.layer);
      pmiEntityKeys.add(entityKey(entity));
    }

    const firstCandidate = snapCandidates.length;
    collectSnapCandidates(entity, arcCircle, snapCandidates);
    if (arcCircle && stitchableRows[stitchableRows.length - 1] === entity) {
      for (let index = firstCandidate; index < snapCandidates.length; index++) {
        if (snapCandidates[index].kind === 'center') {
          arcCenterCandidates.set(stitchableRows.length - 1, index);
        }
      }
    }
  });

  // Text is a real entity on its layer even though it carries no geometry, so it
  // counts — but its box is placed by the text layer, not measured here.
  const texts = assignTextIds(scene.texts);
  texts.forEach(({ text }) => {
    let keys = entityKeysByLayer.get(text.layer);
    if (!keys) {
      keys = new Set();
      entityKeysByLayer.set(text.layer, keys);
    }
    keys.add(entityKey(text));

    entityKeys.add(entityKey(text));
    textEntityKeys.add(entityKey(text));
    // `DxfText` carries no `t`, so the type is recovered from how it lays out:
    // a wrapped row could only have come from MTEXT. Nothing depends on the
    // distinction today — the one consumer is the bending rule, and no text is
    // ever a LINE — but guessing wrong here would be silent, so it is recorded
    // as precisely as the wire format allows.
    recordEntityType(
      entityKey(text),
      text.multiline ? DXF_ENTITY_TYPE.MTEXT : DXF_ENTITY_TYPE.TEXT,
    );
  });

  const measuredBounds = [...boundsByLayer.values()].reduce<Aabb2 | null>(
    (accumulated, layerBounds) => unionAabb(accumulated, layerBounds),
    null,
  );

  const entityCountsByLayer = new Map<number, number>();
  entityKeysByLayer.forEach((keys, layerIndex) => {
    entityCountsByLayer.set(layerIndex, keys.size);
  });

  const bounds = scene.bbox ?? measuredBounds;
  const samePointTolerance = stitchToleranceFor(bounds);
  const { loops, chunkGraph, inputChunks } = stitchLoops(stitchable, {
    tolerance: samePointTolerance,
  });

  const positionsByLoop = new Map<number, number[]>();
  inputChunks.forEach((address, position) => {
    if (!address) {
      return;
    }
    const positions = positionsByLoop.get(address.loopId);
    if (positions) {
      positions.push(position);
    } else {
      positionsByLoop.set(address.loopId, [position]);
    }
  });
  loops.forEach((loop) => {
    const positions = loop.isClosed && positionsByLoop.get(loop.id);
    const circle =
      positions &&
      circleClosedByArcs(
        positions.map((position) => stitchableArcCircles[position]),
      );
    if (!positions || !circle) {
      return;
    }
    positions.forEach((position) => {
      stitchableEdges[position] = circle;
      const candidate = arcCenterCandidates.get(position);
      if (candidate !== undefined) {
        snapCandidates[candidate] = {
          ...snapCandidates[candidate],
          measureEdge: circle,
        };
      }
    });
  });

  // Filtered by key once every row has been seen, so a key is left out exactly
  // when the renderer's PMI toggle would hide it. Measure must never land on
  // dimension or annotation geometry, shown or not.
  const measurableSnapCandidates = snapCandidates.filter(
    (candidate) =>
      candidate.entityId === undefined ||
      !pmiEntityKeys.has(candidate.entityId),
  );

  const measureEdges = new Map<number, (RowMeasureEdges | null)[]>();
  loops.forEach((loop) => {
    measureEdges.set(
      loop.id,
      loop.chunks.map(() => null),
    );
  });
  inputChunks.forEach((address, position) => {
    const edges = address && measureEdges.get(address.loopId);
    if (!edges || pmiEntityKeys.has(stitchable[position].entityId)) {
      return;
    }
    const rowEdges = stitchableEdges[position];
    edges[address.chunkIndex] =
      address.isReversed && Array.isArray(rowEdges)
        ? [...rowEdges].reverse()
        : rowEdges;
  });
  const measureEdgeAt = (
    loopId: number,
    chunkIndex: number,
    segmentIndex: number,
  ): MeasureEdge | null => {
    const edges = measureEdges.get(loopId)?.[chunkIndex] ?? null;
    return Array.isArray(edges) ? edges[segmentIndex] ?? null : edges;
  };

  const diagonal = bounds
    ? Math.hypot(bounds[2] - bounds[0], bounds[3] - bounds[1])
    : 0;

  // Walked after stitching rather than during it: only now is it known which
  // loop each entity's run ended up in. One key can collect several addresses —
  // the rows sharing a key are separate `PolylineInput`s and stitch independently.
  const loopsById = new Map<number, Loop>();
  const chunksByEntityKey = new Map<string, ChunkAddress[]>();
  loops.forEach((loop) => {
    loopsById.set(loop.id, loop);
    loop.chunks.forEach((chunk) => {
      const address = { loopId: loop.id, chunkIndex: chunk.index };
      const addresses = chunksByEntityKey.get(chunk.entityId);
      if (addresses) {
        addresses.push(address);
        return;
      }
      chunksByEntityKey.set(chunk.entityId, [address]);
    });
  });

  // Fills, single-point POINTs and text never reach the stitcher, so they hold a
  // role without ever being pickable or paintable.
  const unstitchedEntityKeys = new Set<string>();
  entityKeys.forEach((key) => {
    if (!chunksByEntityKey.has(key)) {
      unstitchedEntityKeys.add(key);
    }
  });

  return {
    scene,
    texts,
    bounds,
    boundsByLayer,
    entityCountsByLayer,
    layersWithPmi,
    pmiEntityKeys,
    hasPmi: pmiEntityKeys.size > 0,
    loops,
    loopsById,
    chunkGraph,
    index: buildLoopIndex(loops),
    snapIndex: buildSnapIndex(
      measurableSnapCandidates,
      diagonal > 0 ? diagonal * SNAP_CELL_FRACTION : 1,
    ),
    measureEdgeAt,
    samePointTolerance,
    entityKeys: [...entityKeys],
    entityTypesByKey,
    chunksByEntityKey,
    unstitchedEntityKeys,
    textEntityKeys,
  };
};

/**
 * Bounds of the layers that are currently shown.
 *
 * Returns the full extents when every layer is hidden, so fitting an entirely
 * hidden drawing still frames the raw geometry rather than collapsing the view.
 */
export const visibleBounds = (
  model: DrawingModel | null,
  hiddenLayers: ReadonlySet<number>,
): Aabb2 | null => {
  if (!model) {
    return null;
  }
  if (hiddenLayers.size === 0) {
    return model.bounds;
  }

  let bounds: Aabb2 | null = null;
  model.boundsByLayer.forEach((layerBounds, layerIndex) => {
    if (!hiddenLayers.has(layerIndex)) {
      bounds = unionAabb(bounds, layerBounds);
    }
  });

  return bounds ?? model.bounds;
};
